import React from 'react';
import { ArrowUp, Paperclip, Loader2 } from 'lucide-react';
import { useI18n } from '../../i18n';

interface AiChatInputProps {
  onSend: (message: string) => Promise<void> | void;
  isLoading?: boolean;
  placeholder?: string;
  allowImageAttach?: boolean;
  onImageAttach?: () => void;
  attachedImagesCount?: number;
  imagePreviewUrls?: string[];
  onRemoveImage?: (index: number) => void;
  value?: string;
  onValueChange?: (value: string) => void;
}

export const AiChatInput: React.FC<AiChatInputProps> = ({
  onSend,
  isLoading = false,
  placeholder,
  allowImageAttach = false,
  onImageAttach,
  attachedImagesCount = 0,
  imagePreviewUrls = [],
  onRemoveImage,
  value,
  onValueChange,
}) => {
  const { t } = useI18n();
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState(value ?? '');

  React.useEffect(() => {
    if (isControlled) {
      setInternalValue(value ?? '');
    }
  }, [isControlled, value]);

  const draft = isControlled ? value ?? '' : internalValue;

  const updateDraft = (next: string) => {
    if (onValueChange) {
      onValueChange(next);
    }
    if (!isControlled) {
      setInternalValue(next);
    }
  };

  const handleSend = async () => {
    const messageToSend = (draft || '').trim();
    if (!messageToSend && attachedImagesCount === 0) return;

    if (!isControlled) {
      setInternalValue('');
    } else if (onValueChange) {
      onValueChange('');
    }

    const result = onSend(messageToSend);
    if (result && typeof result.then === 'function') {
      await result;
    }
  };

  const canSend = ((draft || '').trim().length > 0 || attachedImagesCount > 0) && !isLoading;

  return (
    <div className="space-y-2">
      {imagePreviewUrls.length > 0 && (
        <div className="flex gap-2 px-2">
          {imagePreviewUrls.map((url, index) => (
            <div
              key={index}
              className="relative w-16 h-16 rounded-lg overflow-hidden"
              style={{
                border: '1px solid rgba(0,0,0,0.1)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}
            >
              <img src={url} alt={t('attachmentAlt', { count: index + 1 })} className="w-full h-full object-cover" />
              {onRemoveImage && (
                <button
                  onClick={() => onRemoveImage(index)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center"
                  style={{
                    background: 'rgba(0,0,0,0.6)',
                  }}
                >
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div
        className="rounded-[20px]"
        style={{
          background: 'linear-gradient(145deg, rgba(18,18,20,0.98) 0%, rgba(12,12,14,0.99) 100%)',
          border: '1px solid rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2), inset 0 2px 0 rgba(255,255,255,0.04), inset 0 -3px 6px rgba(0,0,0,0.5)',
        }}
      >
        <div className="flex items-end gap-2 p-3">
          {allowImageAttach && onImageAttach && attachedImagesCount < 3 && (
            <button
              onClick={onImageAttach}
              disabled={isLoading}
              className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-opacity"
              style={{
                background: 'rgba(255,255,255,0.06)',
                color: 'rgba(255,255,255,0.6)',
                opacity: isLoading ? 0.4 : 1,
                transition: 'all 0.2s cubic-bezier(0.25, 0.1, 0.25, 1)',
              }}
              aria-label={t('attachPhotoAria')}
            >
              <Paperclip size={18} strokeWidth={2} />
            </button>
          )}

          <div className="flex-1">
            <textarea
              value={draft}
              onChange={(e) => updateDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={placeholder || t('yourQuestion')}
              disabled={isLoading}
              rows={1}
              className="ai-chat-input-invisible"
              style={{
                width: '100%',
                minHeight: '24px',
                maxHeight: '120px',
                padding: 0,
                margin: 0,
                resize: 'none',
                color: 'rgba(255,255,255,0.9)',
                fontSize: '15px',
                lineHeight: '1.4',
                fontFamily: 'inherit',
                fontWeight: 'inherit',
              }}
            />
          </div>

          <button
            onClick={handleSend}
            disabled={!canSend}
            className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all"
            style={{
              background: canSend
                ? 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(235,235,245,0.92) 100%)'
                : 'rgba(255,255,255,0.08)',
              color: canSend ? 'rgba(0,0,0,0.88)' : 'rgba(255,255,255,0.3)',
              boxShadow: canSend 
                ? '0 4px 16px rgba(255,255,255,0.25), inset 0 1px 0 rgba(255,255,255,0.4)' 
                : 'none',
              transition: 'all 0.2s cubic-bezier(0.25, 0.1, 0.25, 1)',
            }}
          >
            {isLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <ArrowUp size={18} strokeWidth={2.5} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
